import React, { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Video, FileText, BookOpen, Trash2, Edit, X, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState("videos");
  const [viewMode, setViewMode] = useState("list");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  // Content lists
  const [videos, setVideos] = useState([]);
  const [articles, setArticles] = useState([]);

  // Simplified forms
  const [videoForm, setVideoForm] = useState({
    title: "",
    youtube_url: "",
    description: "",
    category: "Patient Education"
  });

  const [articleForm, setArticleForm] = useState({
    title: "",
    content: "",
    author: "Dr. Salvatore Catarinicchia",
    category: "Patient Education"
  });

  const videoCategories = ["Our Practice", "Patient Education", "Procedures", "Protocols"];
  const articleCategories = ["Prostate Health", "Bladder Health", "Kidney Health", "Men's Health", "Sexual Health", "Infections", "Symptoms", "Surgical Conditions", "Cancer", "Emergencies"];

  useEffect(() => {
    loadContent();
  }, [activeTab]);

  const loadContent = async () => {
    setLoading(true);
    try {
      if (activeTab === "videos") {
        const res = await axios.get(`${API}/videos?limit=100`);
        setVideos(res.data);
      } else if (activeTab === "articles") {
        const res = await axios.get(`${API}/blog?limit=100`);
        setArticles(res.data);
      }
    } catch (error) {
      console.error("Error loading content:", error);
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg, type = "success") => {
    setMessage({ text: msg, type });
    setTimeout(() => setMessage(""), 3000);
  };

  // Video handlers
  const handleSaveVideo = async (e) => {
    e.preventDefault();
    if (!videoForm.title || !videoForm.youtube_url) {
      showMessage("Title and YouTube URL are required", "error");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        title: videoForm.title,
        youtube_url: videoForm.youtube_url,
        description: videoForm.description || "",
        category: videoForm.category
      };
      
      if (editingItem) {
        await axios.put(`${API}/videos/${editingItem.id}`, payload);
        showMessage("Video updated!");
      } else {
        await axios.post(`${API}/videos`, payload);
        showMessage("Video added!");
      }
      resetVideoForm();
      loadContent();
    } catch (error) {
      console.error("Error saving video:", error);
      showMessage("Error saving video: " + (error.response?.data?.detail || error.message), "error");
    } finally {
      setLoading(false);
    }
  };

  const resetVideoForm = () => {
    setVideoForm({ title: "", youtube_url: "", description: "", category: "Patient Education" });
    setEditingItem(null);
    setViewMode("list");
  };

  const editVideo = (video) => {
    setVideoForm({
      title: video.title,
      youtube_url: video.youtube_url || video.url || "",
      description: video.description || "",
      category: video.category || "Patient Education"
    });
    setEditingItem(video);
    setViewMode("add");
  };

  // Article handlers
  const handleSaveArticle = async (e) => {
    e.preventDefault();
    if (!articleForm.title || !articleForm.content) {
      showMessage("Title and content are required", "error");
      return;
    }
    setLoading(true);
    try {
      if (editingItem) {
        await axios.put(`${API}/blog/${editingItem.id}`, articleForm);
        showMessage("Article updated!");
      } else {
        await axios.post(`${API}/blog`, articleForm);
        showMessage("Article published!");
      }
      resetArticleForm();
      loadContent();
    } catch (error) {
      showMessage("Error saving article", "error");
    } finally {
      setLoading(false);
    }
  };

  const resetArticleForm = () => {
    setArticleForm({ title: "", content: "", author: "Dr. Salvatore Catarinicchia", category: "Patient Education" });
    setEditingItem(null);
    setViewMode("list");
  };

  const editArticle = (article) => {
    setArticleForm({
      title: article.title,
      content: article.content,
      author: article.author,
      category: article.category || "Patient Education"
    });
    setEditingItem(article);
    setViewMode("add");
  };

  // Delete handler
  const handleDelete = async (type, id) => {
    setLoading(true);
    try {
      const endpoint = type === "videos" ? "videos" : "blog";
      await axios.delete(`${API}/${endpoint}/${id}`);
      showMessage("Deleted successfully!");
      setDeleteConfirm(null);
      loadContent();
    } catch (error) {
      showMessage("Error deleting item", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-panel">
      {/* Message */}
      {message && (
        <div className={`admin-message ${message.type}`}>
          {message.type === "success" ? <Check size={16} /> : <X size={16} />}
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === "videos" ? "active" : ""}`}
          onClick={() => { setActiveTab("videos"); setViewMode("list"); setEditingItem(null); }}
        >
          <Video size={18} /> Videos
        </button>
        <button
          className={`admin-tab ${activeTab === "articles" ? "active" : ""}`}
          onClick={() => { setActiveTab("articles"); setViewMode("list"); setEditingItem(null); }}
        >
          <BookOpen size={18} /> Articles
        </button>
      </div>

      {/* Content */}
      <div className="admin-content">
        {/* VIDEOS TAB */}
        {activeTab === "videos" && (
          <>
            {viewMode === "list" ? (
              <>
                <div className="admin-toolbar">
                  <h3>Manage Videos</h3>
                  <button className="btn-add" onClick={() => setViewMode("add")}>
                    <Plus size={18} /> Add Video
                  </button>
                </div>
                
                {loading ? (
                  <p className="loading-text">Loading...</p>
                ) : videos.length === 0 ? (
                  <p className="empty-text">No videos yet</p>
                ) : (
                  <div className="admin-list">
                    {videos.map((video) => (
                      <div key={video.id} className="admin-item">
                        <div className="item-info">
                          <h4>{video.title}</h4>
                          <span className="item-category">{video.category}</span>
                        </div>
                        <div className="item-actions">
                          <button onClick={() => editVideo(video)} title="Edit">
                            <Edit size={16} />
                          </button>
                          <button onClick={() => setDeleteConfirm({ type: "videos", id: video.id })} title="Delete">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="admin-form-container">
                <h3>{editingItem ? "Edit Video" : "Add Video"}</h3>
                <form onSubmit={handleSaveVideo} className="admin-form">
                  <div className="form-group">
                    <label>Title *</label>
                    <input
                      type="text"
                      value={videoForm.title}
                      onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
                      placeholder="What to Expect During a Cystoscopy"
                    />
                  </div>
                  <div className="form-group">
                    <label>YouTube URL *</label>
                    <input
                      type="text"
                      value={videoForm.youtube_url}
                      onChange={(e) => setVideoForm({ ...videoForm, youtube_url: e.target.value })}
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                  </div>
                  <div className="form-group">
                    <label>Category *</label>
                    <select
                      value={videoForm.category}
                      onChange={(e) => setVideoForm({ ...videoForm, category: e.target.value })}
                    >
                      {videoCategories.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Description (optional)</label>
                    <textarea
                      value={videoForm.description}
                      onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })}
                      placeholder="Brief description..."
                      rows={3}
                    />
                  </div>
                  <div className="form-buttons">
                    <button type="button" className="btn-cancel" onClick={resetVideoForm}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-save" disabled={loading}>
                      {loading ? "Saving..." : (editingItem ? "Update" : "Publish")}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </>
        )}

        {/* ARTICLES TAB */}
        {activeTab === "articles" && (
          <>
            {viewMode === "list" ? (
              <>
                <div className="admin-toolbar">
                  <h3>Manage Articles</h3>
                  <button className="btn-add" onClick={() => setViewMode("add")}>
                    <Plus size={18} /> Add Article
                  </button>
                </div>
                
                {loading ? (
                  <p className="loading-text">Loading...</p>
                ) : articles.length === 0 ? (
                  <p className="empty-text">No articles yet</p>
                ) : (
                  <div className="admin-list">
                    {articles.map((article) => (
                      <div key={article.id} className="admin-item">
                        <div className="item-info">
                          <h4>{article.title}</h4>
                          <span className="item-category">{article.category}</span>
                          <span className="item-author">by {article.author}</span>
                        </div>
                        <div className="item-actions">
                          <button onClick={() => editArticle(article)} title="Edit">
                            <Edit size={16} />
                          </button>
                          <button onClick={() => setDeleteConfirm({ type: "articles", id: article.id })} title="Delete">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="admin-form-container">
                <h3>{editingItem ? "Edit Article" : "Add Article"}</h3>
                <form onSubmit={handleSaveArticle} className="admin-form">
                  <div className="form-group">
                    <label>Title *</label>
                    <input
                      type="text"
                      value={articleForm.title}
                      onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                      placeholder="Article title"
                    />
                  </div>
                  <div className="form-group">
                    <label>Category *</label>
                    <select
                      value={articleForm.category}
                      onChange={(e) => setArticleForm({ ...articleForm, category: e.target.value })}
                    >
                      {articleCategories.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Author</label>
                    <input
                      type="text"
                      value={articleForm.author}
                      onChange={(e) => setArticleForm({ ...articleForm, author: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Content * (Markdown supported)</label>
                    <textarea
                      value={articleForm.content}
                      onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })}
                      placeholder="### What Is This Condition?&#10;&#10;Content here..."
                      rows={15}
                    />
                  </div>
                  <div className="form-buttons">
                    <button type="button" className="btn-cancel" onClick={resetArticleForm}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-save" disabled={loading}>
                      {loading ? "Saving..." : (editingItem ? "Update" : "Publish")}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h4>Confirm Delete</h4>
            <p>Are you sure you want to delete this item? This cannot be undone.</p>
            <div className="modal-buttons">
              <button className="btn-cancel" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
              <button 
                className="btn-delete" 
                onClick={() => handleDelete(deleteConfirm.type, deleteConfirm.id)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .admin-panel {
          background: #fff;
        }
        .admin-message {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          border-radius: 6px;
          margin-bottom: 1rem;
          font-size: 0.95rem;
        }
        .admin-message.success {
          background: #c6f6d5;
          color: #22543d;
        }
        .admin-message.error {
          background: #fed7d7;
          color: #742a2a;
        }
        .admin-tabs {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 1rem;
        }
        .admin-tab {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.6rem 1rem;
          border: 1px solid #e2e8f0;
          background: #f7fafc;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.95rem;
          color: #4a5568;
        }
        .admin-tab.active {
          background: #1a365d;
          color: #fff;
          border-color: #1a365d;
        }
        .admin-toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }
        .admin-toolbar h3 {
          color: #1a365d;
          font-size: 1.1rem;
        }
        .btn-add {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.5rem 1rem;
          background: #1a365d;
          color: #fff;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.9rem;
        }
        .btn-add:hover {
          background: #2c5282;
        }
        .loading-text, .empty-text {
          text-align: center;
          color: #718096;
          padding: 2rem;
        }
        .admin-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .admin-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 1rem;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          background: #f7fafc;
        }
        .item-info h4 {
          font-size: 0.95rem;
          color: #1a365d;
          margin-bottom: 0.25rem;
        }
        .item-category {
          font-size: 0.8rem;
          color: #718096;
          background: #edf2f7;
          padding: 0.15rem 0.5rem;
          border-radius: 4px;
          margin-right: 0.5rem;
        }
        .item-author {
          font-size: 0.8rem;
          color: #718096;
        }
        .item-actions {
          display: flex;
          gap: 0.5rem;
        }
        .item-actions button {
          padding: 0.4rem;
          background: none;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          cursor: pointer;
          color: #4a5568;
        }
        .item-actions button:hover {
          background: #edf2f7;
        }
        .admin-form-container h3 {
          color: #1a365d;
          margin-bottom: 1.5rem;
        }
        .admin-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
        }
        .form-group label {
          font-size: 0.9rem;
          color: #4a5568;
          font-weight: 500;
        }
        .form-group input,
        .form-group select,
        .form-group textarea {
          padding: 0.6rem 0.75rem;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          font-size: 0.95rem;
        }
        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #1a365d;
        }
        .form-group textarea {
          resize: vertical;
          font-family: inherit;
        }
        .form-buttons {
          display: flex;
          gap: 0.75rem;
          margin-top: 0.5rem;
        }
        .btn-cancel {
          padding: 0.6rem 1.25rem;
          background: #edf2f7;
          color: #4a5568;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          cursor: pointer;
        }
        .btn-save {
          padding: 0.6rem 1.25rem;
          background: #1a365d;
          color: #fff;
          border: none;
          border-radius: 6px;
          cursor: pointer;
        }
        .btn-save:disabled {
          opacity: 0.6;
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal-box {
          background: #fff;
          padding: 1.5rem;
          border-radius: 8px;
          max-width: 400px;
          width: 90%;
        }
        .modal-box h4 {
          color: #1a365d;
          margin-bottom: 0.75rem;
        }
        .modal-box p {
          color: #4a5568;
          margin-bottom: 1.25rem;
        }
        .modal-buttons {
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
        }
        .btn-delete {
          padding: 0.5rem 1rem;
          background: #c53030;
          color: #fff;
          border: none;
          border-radius: 6px;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default AdminPanel;
